import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { ProductCodeDto } from "@/features/ledger/api/dto";
import { ProductCodeRowActions } from "@/features/ledger/components/product-codes/product-code-row-actions";
import { PRODUCT_CATEGORIES } from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type ProductCodeRow = ProductCodeDto & { id: string };

/**
 * Colonnes des codes produits (`POST /ProductCodes/all`). Filtres :
 * `code` (texte) et `category` (select). Codes en `NoTranslate`.
 */
export const productCodesColumns: ColumnsClarFactory<ProductCodeRow> = (
  navigate
) => [
  {
    id: "code",
    accessorKey: "code",
    header: t("ledger.productCodes.columns.code"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "label",
    accessorKey: "label",
    header: t("ledger.productCodes.columns.label"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "category",
    accessorKey: "category",
    header: t("ledger.productCodes.columns.category"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: {
      type: "select",
      options: PRODUCT_CATEGORIES.map((value) => ({ value, label: value })),
    },
  },
  {
    id: "isActive",
    accessorKey: "isActive",
    header: t("ledger.productCodes.columns.isActive"),
    cell: ({ getValue }) => (
      <Badge variant={getValue() ? "success" : "secondary"}>
        {getValue() ? t("ledger.common.active") : t("ledger.common.inactive")}
      </Badge>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <ProductCodeRowActions productCode={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
